import React, { useState } from 'react';

import { useMutateCreatePreset } from '@hooks/cmModule/useMutateCreatePreset';
import { useMutateEditPreset } from '@hooks/cmModule/useMutateEditPreset';
import { useMutateExportMoBranches } from '@hooks/cmModule/useMutateExportMoBranches';
import { useMutateRemovePreset } from '@hooks/cmModule/useMutateRemovePreset';
import { useMutateSelectedPresetTree } from '@hooks/cmModule/useMutateSelectedPresetTree';
import { useMutateStartProgress } from '@hooks/cmModule/useMutateStartProgress';
import { useMutateTree } from '@hooks/cmModule/useMutateTree';
import { useQueryCcDates } from '@hooks/cmModule/useQueryCcDates';
import { useQueryCcMavenirDates } from '@hooks/cmModule/useQueryCcMavenirDates';
import { useQueryConfigurationComparisonConfigTypes } from '@hooks/cmModule/useQueryConfigurationComparisonConfigType';
import { useQueryConfigurationComparisonNodes } from '@hooks/cmModule/useQueryConfigurationComparisonNodes';
import {
	useQueryConfigurationComparisonReleaseVersions,
} from '@hooks/cmModule/useQueryConfigurationComparisonReleaseVersion';
import {
	useQueryConfigurationComparisonTechnologies,
} from '@hooks/cmModule/useQueryConfigurationComparisonTechnologies';
import { useQueryConfigurationComparisonVendors } from '@hooks/cmModule/useQueryConfigurationComparisonVendors';
import { useQueryMoBranches } from '@hooks/cmModule/useQueryMoBranches';
import { useQueryMoPresetBranches } from '@hooks/cmModule/useQueryMoPresetBranches';
import { useQueryNodeTypes } from '@hooks/cmModule/useQueryNodeTypes';
import { useQueryPresets } from '@hooks/cmModule/useQueryPresets';
import { useQueryProgress } from '@hooks/cmModule/useQueryProgress';
import { useQuerySiteIds } from '@hooks/cmModule/useQuerySiteIds';
import { PROGRESS_INTERVAL } from '@hooks/constants';

import {
	IPresetParams,
	ISubmittedPathParamsProps,
	TCMDefaultDTO,
	TMavenirDTO,
} from '@interfaces/cmModule/dto/cmModuleDTO';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';
import { ITreePreset } from '@interfaces/components/tree';

type TExtendsParams = TCMDefaultDTO & TDefaultDTO & TMavenirDTO & IPresetParams & ISubmittedPathParamsProps;

interface IUseConfigurationComparisonParams extends TExtendsParams {
	isDifferenceOnly: boolean;
	toggleMOTree: () => void;
	setSavedPreset: React.Dispatch<React.SetStateAction<ITreePreset | undefined>>;
}

export const useConfigurationComparison = (params: IUseConfigurationComparisonParams) => {
	const [refetchIntervalProgress, setRefetchIntervalProgress]
		= useState<number | false>(PROGRESS_INTERVAL);

	const resetIntervals = () => setRefetchIntervalProgress(PROGRESS_INTERVAL);

	const {
		vendor,
		technology,
		nodeType,
		siteIds,
		toggleMOTree,
		submittedParams,
		submittedPath,
		isDifferenceOnly,
		pageSize,
		page,
		sortDirection,
		sortFieldName,
		searchValue,
		setSavedPreset,
		savedPreset,
		selectedPreset,
		isPresetTableView,
		nodes,
		version,
		type,
	} = params || {};
	const { tree, isLoadingGenerateTree, generateTree } = useMutateTree(toggleMOTree);

	const { progressId, runProgress } = useMutateStartProgress({
		onSettledCallback: resetIntervals,
		data: {
			categoryPaths: submittedPath,
			dumpId: tree?.treeIds,
			onlyChanged: isDifferenceOnly,
			...submittedParams,
			searchValue,
		},
		savedPreset,
	});

	const { progress } = useQueryProgress({
		progressId,
		refetchIntervalProgress,
		setRefetchIntervalProgress,
	});

	const { branches, isLoadingBranches, isFetchingBranches } = useQueryMoBranches({
		onlyChanged: isDifferenceOnly,
		page,
		pageSize,
		progressId,
		searchValue,
		selectedPreset,
		setRefetchIntervalProgress,
		sortDirection,
		sortFieldName,
		submittedParams,
		submittedPath,
		tree,
		progress,
	});

	const { presetBranches, isLoadingPresetBranches, isFetchingPresetBranches } = useQueryMoPresetBranches({
		isDifferenceOnly,
		isPresetTableView,
		page,
		pageSize,
		progressId,
		savedPreset,
		searchValue,
		setRefetchIntervalProgress,
		sortDirection,
		sortFieldName,
		submittedPath,
	});

	const { presetsTreeOptions } = useQueryPresets({
		nodeType,
		technology,
		vendor,
	});

	const { exportBranches } = useMutateExportMoBranches(tree);

	const { createTreePreset, isLoadingCreateTreePreset } = useMutateCreatePreset(tree);
	const { getPresetTree, isLoadingSelectedPresetTree } = useMutateSelectedPresetTree(setSavedPreset);
	const { vendorsOptions, isLoadingVendorsOptions } = useQueryConfigurationComparisonVendors();
	const { technologiesOptions, isLoadingTechnologiesOptions } = useQueryConfigurationComparisonTechnologies(vendor);
	const { updateTreePreset, isLoadingUpdateTreePreset } = useMutateEditPreset();
	const { deleteTreePreset, isLoadingDeleteTreePreset } = useMutateRemovePreset();
	const { nodeTypesOptions, isLoadingNodeTypesOptions } = useQueryNodeTypes({ vendor, technology });
	const { siteIdsOptions, isLoadingSiteIdsOptions } = useQuerySiteIds({ vendor, technology, nodeType });
	const { datesOptions, isLoadingDatesOptions } = useQueryCcDates({ vendor, technology, nodeType, siteIds });
	const { nodesOptions, isLoadingNodesOptions } = useQueryConfigurationComparisonNodes(vendor);
	const { releaseVersionsOptions, isLoadingReleaseVersionsOptions } =
		useQueryConfigurationComparisonReleaseVersions({ vendor, nodes });
	const { configTypesOptions, isLoadingConfigTypesOptions } =
		useQueryConfigurationComparisonConfigTypes({ version, nodes, vendor });
	const { datesMavenirOptions, isLoadingMavenirDatesOptions } =
		useQueryCcMavenirDates({ vendor, nodes, version, type });

	return {
		branchesList: selectedPreset ? presetBranches?.items || [] : branches?.items || [],
		presetsTreeOptions: (!!nodeType && !!technology && !!vendor) ? presetsTreeOptions || [] : [],
		countOfRecords: selectedPreset ? presetBranches?.count || 0 : branches?.count || 0,
		moTree: tree?.moTree,
		treeId: tree?.treeIds,
		isLoadingVendors: isLoadingVendorsOptions,
		isLoadingTechnologies: isLoadingTechnologiesOptions,
		isLoadingNodeTypes: isLoadingNodeTypesOptions,
		isLoadingSiteIds: isLoadingSiteIdsOptions,
		isLoadingDates: isLoadingDatesOptions,
		isLoadingNodesOptions: isLoadingNodesOptions,
		isLoadingOptions: (
			isLoadingVendorsOptions ||
			isLoadingTechnologiesOptions ||
			isLoadingNodeTypesOptions ||
			isLoadingSiteIdsOptions ||
			isLoadingDatesOptions ||
			isLoadingGenerateTree ||
			isLoadingSelectedPresetTree ||
			isLoadingCreateTreePreset ||
			isLoadingCreateTreePreset ||
			isLoadingUpdateTreePreset ||
			isLoadingDeleteTreePreset ||
			isLoadingNodesOptions ||
			isLoadingReleaseVersionsOptions ||
			isLoadingConfigTypesOptions ||
			isLoadingMavenirDatesOptions
		),
		isLoadingBranches: selectedPreset ? (isLoadingPresetBranches || isFetchingPresetBranches)
			: (isLoadingBranches || isFetchingBranches),
		vendorsOptions: vendorsOptions || [],
		technologiesOptions: technologiesOptions || [],
		nodeTypesOptions: nodeTypesOptions || [],
		nodesOptions,
		releaseVersionsOptions,
		configTypesOptions,
		datesMavenirOptions,
		siteIdsOptions: siteIdsOptions || [],
		datesOptions: datesOptions || [],
		generateTree,
		runProgress,
		progress,
		exportBranches,
		createTreePreset,
		getPresetTree,
		updateTreePreset,
		deleteTreePreset,
	};
};
